import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Info } from 'lucide-react';
import { StatusIcon } from '@/lib/executionStatus';
import { useExecutionHistory } from '@/hooks/useConnectorExecutions';
import { useConnectorDetailPageState } from '@/hooks/useConnectorDetailPageState';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { formatOutputCounts } from '@/utils/formatOutputCounts';
import type { ConnectorExecution } from '@/types/connectors';

interface Props { connectorType: string; name: string; }

// Render a duration (in seconds) as `Xm Ys`, expanding to `Xh Ym Zs` once it
// reaches an hour. Rounds to the nearest whole second before decomposing.
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
}

function summary(e: ConnectorExecution): string {
  if (e.status === 'failed')   return (e as any).error_message ?? 'failed';
  if (e.status === 'running' || e.status === 'pending') {
    const r = (e.records_processed ?? 0).toLocaleString();
    return `running · ${r} rows so far`;
  }
  const dur = e.duration_seconds ? formatDuration(e.duration_seconds) : '—';
  // Terminal-but-not-success statuses get an explicit label. #1009: a partial
  // run's error_message names the misconfigured setting — that message is the
  // whole point of the degradation, so show it here rather than in metadata.
  if (e.status === 'cancelled' || e.status === 'timeout' || e.status === 'skipped' || e.status === 'partial') {
    const reason = (e as any).error_message;
    return reason ? `${e.status} · ${dur} · ${reason}` : `${e.status} · ${dur}`;
  }
  const rows = (e.records_processed ?? 0).toLocaleString();
  // #2235: for a run that merged into tables, files_created counts merges, not
  // files; the run page's Files tile carries the table's real file count.
  const tables = e.tables_updated ?? 0;
  const outputs = formatOutputCounts(tables > 0 ? 0 : (e.files_created ?? 0), tables);
  return `${dur} · ${rows} rows · ${outputs}`;
}

export function ExecutionList({ connectorType, name }: Props) {
  const { data, isLoading, error } = useExecutionHistory(connectorType, name, 1, 50);
  const { executionId, setExecutionId } = useConnectorDetailPageState();
  const [query, setQuery] = useState('');

  const executions = data?.executions ?? [];
  const filtered = useMemo(() => {
    if (!query.trim()) return executions;
    const q = query.toLowerCase();
    return executions.filter((e) => {
      const haystack = [
        e.status,
        (e as any).error_message ?? '',
        e.started_at ?? '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [executions, query]);

  if (isLoading) return <div className="text-sm text-muted-foreground p-3">Loading…</div>;
  if (error)     return <div className="text-sm text-destructive p-3">{(error as Error).message}</div>;
  if (!executions.length) return <div className="text-sm text-muted-foreground p-3">No executions yet.</div>;

  return (
    <div className="space-y-2">
      <Input
        type="search"
        role="searchbox"
        placeholder="Search executions"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul role="listbox" aria-label="Executions" className="space-y-2">
        {filtered.map((e) => {
          const selected = e.execution_id === executionId;
          return (
            <li
              key={e.execution_id}
              role="option"
              aria-selected={selected}
              tabIndex={0}
              onClick={() => setExecutionId(e.execution_id)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  setExecutionId(e.execution_id);
                }
              }}
              className={cn('flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors hover:bg-accent',
                            selected && 'border-primary bg-primary/5 ring-2 ring-primary/20 hover:bg-primary/5')}
            >
              <StatusIcon status={e.status} size="h-4 w-4" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{format(new Date(e.started_at!), 'MMM d, h:mm a')}</span>
                  {e.trigger_type && (
                    <span className="rounded-full border px-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {e.trigger_type}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">{summary(e)}</div>
                {e.status === 'success' && ((e as any).zero_row_resources?.length ?? 0) > 0 && (
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-info"
                       data-testid="zero-row-row-marker">
                    <Info className="h-3.5 w-3.5" aria-hidden />
                    {(e as any).zero_row_resources.length} resource
                    {(e as any).zero_row_resources.length === 1 ? '' : 's'} wrote no output
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
